import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  useTheme,
} from '@mui/material';
import { ShowChart, TrendingUp, TrendingDown } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { formattedSymbol, itemVariants } from './utils';

/**
 * PerformanceWidget - Displays market performance metrics
 * 
 * @param {Object} config - Widget configuration
 * @param {string} [config.title] - Custom title (default: "Market Performance")
 * @param {number|string} [config.maxHeight] - Maximum height for widget
 */
const PerformanceSkeletonRows = () => (
  <List sx={{ px: 0 }}>
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <ListItem key={i} sx={{ px: 0, py: 1 }}>
        <ListItemIcon sx={{ minWidth: 40 }}>
          <Skeleton variant="circular" width={24} height={24} sx={{ bgcolor: 'var(--dwlf-ink-06)' }} />
        </ListItemIcon>
        <ListItemText
          primary={<Skeleton width="40%" sx={{ bgcolor: 'var(--dwlf-ink-06)' }} />}
          secondary={<Skeleton width="60%" sx={{ bgcolor: 'var(--dwlf-ink-04)' }} />}
        />
      </ListItem>
    ))}
  </List>
);

const PerformanceWidget = ({ config = {} }) => {
  const theme = useTheme();
  const { performanceSummary, performanceSummaryLoading } = useSelector((state) => state.markets);

  const title = config.title || 'Market Performance';
  const maxHeight = config.maxHeight || '300px';
  const isLoading = !!performanceSummaryLoading;

  // No mount-level dispatch — the page-level fetchMarketsOverview
  // thunk provides performance data as part of the combined
  // response. Dispatching fetchPerformanceSummary here would race
  // with the overview thunk (React fires child effects before
  // parent effects) and cause a redundant /performance-summary
  // request on every page load.

  // Show skeleton while loading, empty state when loaded with no data
  if (!performanceSummary) {
    return (
      <motion.div variants={itemVariants}>
        <Card sx={{ width: '100%', height: '400px', display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShowChart sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  {title}
                </Typography>
              </Box>
            </Box>
            {isLoading ? (
              <PerformanceSkeletonRows />
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                flexGrow={1}
                gap={2}
              >
                <ShowChart sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  No performance data available
                </Typography>
                <Typography variant="caption" color="text.disabled" textAlign="center">
                  Performance metrics will appear here once data is available
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  const performanceEntries = [
    { label: 'Best YTD', data: performanceSummary.ytdChange.best, isPositive: true },
    { label: 'Worst YTD', data: performanceSummary.ytdChange.worst, isPositive: false },
    { label: 'Best 30D', data: performanceSummary.last30DaysChange.best, isPositive: true },
    { label: 'Worst 30D', data: performanceSummary.last30DaysChange.worst, isPositive: false },
    ...(performanceSummary.last5CandlesChange ? [
      { label: 'Best 5D', data: performanceSummary.last5CandlesChange.best, isPositive: true },
      { label: 'Worst 5D', data: performanceSummary.last5CandlesChange.worst, isPositive: false },
    ] : []),
    ...(performanceSummary.latestCandleChange ? [
      { label: 'Best 1D', data: performanceSummary.latestCandleChange.best, isPositive: true },
      { label: 'Worst 1D', data: performanceSummary.latestCandleChange.worst, isPositive: false },
    ] : []),
  ].filter((entry) => entry?.data?.symbol);

  return (
    <motion.div variants={itemVariants}>
      <Card sx={{ width: '100%', height: '400px', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShowChart sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="h6" fontWeight={600} color="text.primary">
                {title}
              </Typography>
            </Box>
            <Chip 
              label={performanceEntries.length} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          </Box>

          {/* Performance List */}
          <List sx={{ flexGrow: 1, maxHeight, overflow: 'auto', px: 0 }}>
            {performanceEntries.map(({ label, data, isPositive }, index) => (
              <Link 
                key={index} 
                to={`/markets/${formattedSymbol(data.symbol)}`} 
                style={{ textDecoration: 'none' }}
              >
                <ListItem 
                  sx={{ 
                    px: 0, 
                    py: 1,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: (theme.vars || theme).palette.action.hover,
                      borderRadius: 1
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Box sx={{ color: isPositive ? 'success.main' : 'error.main', fontSize: 20 }}>
                      {isPositive ? <TrendingUp /> : <TrendingDown />}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                          {data.symbol}
                        </Typography>
                        <Chip
                          label={label}
                          size="small"
                          color={isPositive ? 'success' : 'error'}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: '18px' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color={isPositive ? 'success.main' : 'error.main'} fontWeight={600}>
                          {isPositive ? '+' : ''}{data.change.toFixed(2)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {data.name}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              </Link>
            ))}
          </List>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default PerformanceWidget;
